import { useEffect } from "react";
import { LANGS, useLang } from "@/i18n";

const LOCALES = { it: "it_IT", en: "en_GB", fr: "fr_FR" };
export const langUrl = (origin, path, lang) => `${origin}${path}${lang === "it" ? "" : `${path.includes("?") ? "&" : "?"}lang=${lang}`}`;

const addAlternates = (origin, path) => {
  const links = [...LANGS.map((l) => [l, langUrl(origin, path, l)]), ["x-default", langUrl(origin, path, "it")]].map(([hl, href]) => {
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = hl;
    el.href = href;
    el.dataset.alt = "1";
    document.head.appendChild(el);
    return el;
  });
  return () => links.forEach((el) => el.remove());
};

// Injects one JSON-LD block (structured data for Google) while the page is mounted.
export const useJsonLd = (id, data) => {
  const json = JSON.stringify(data);
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.ld = id;
    el.text = json;
    document.head.appendChild(el);
    return () => el.remove();
  }, [id, json]);
};

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  const prev = el?.getAttribute(attr) ?? null;
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    const [, k, v] = selector.match(/\[([\w:]+)="([^"]+)"\]/);
    el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
  return () => (prev === null ? el.remove() : el.setAttribute(attr, prev));
};

// Per-page title, description, canonical and share card (Open Graph + Twitter).
export const usePageMeta = ({ title, desc, path, image, imageAlt, type = "website", brand = "Emma Industrie", ogTitle, ogDesc }) => {
  const { lang } = useLang();
  useEffect(() => {
    const origin = window.location.origin;
    const url = langUrl(origin, path, lang);
    const img = `${origin}${image}`;
    const prevTitle = document.title;
    document.title = `${title} | ${brand}`;
    const undo = [
      setMeta('meta[name="description"]', "content", desc),
      setMeta('link[rel="canonical"]', "href", url),
      setMeta('meta[property="og:type"]', "content", type),
      setMeta('meta[property="og:title"]', "content", ogTitle ?? title),
      setMeta('meta[property="og:description"]', "content", ogDesc ?? desc),
      setMeta('meta[property="og:url"]', "content", url),
      setMeta('meta[property="og:image"]', "content", img),
      setMeta('meta[property="og:image:alt"]', "content", imageAlt ?? title),
      setMeta('meta[name="twitter:title"]', "content", ogTitle ?? title),
      setMeta('meta[name="twitter:description"]', "content", ogDesc ?? desc),
      setMeta('meta[name="twitter:image"]', "content", img),
      setMeta('meta[property="og:locale"]', "content", LOCALES[lang]),
      addAlternates(origin, path),
    ];
    return () => {
      document.title = prevTitle;
      undo.forEach((fn) => fn());
    };
  }, [title, desc, path, image, imageAlt, type, brand, ogTitle, ogDesc, lang]);
};
